import { MenuItem } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { AllocationMethodologyTitle } from "features/investments/AllocationMethodologyTitle";
import { formatInvestmentLocationCountry } from "features/investments/InvestmentUtils";
import {
  PortfolioFormAdditionalFields,
  PortfolioFormRequiredFields,
} from "features/investments/portfolio/PortfolioFormFields";
import { PortfolioTabProps } from "features/investments/PortfolioTabProps";
import {
  getInvestmentTableDataRetrieveQueryKey,
  useInvestmentRetrieveSuspense,
  useInvestmentUpdate,
} from "gen/endpoints/investment/investment";
import { InvestmentEntityCreateEditAPIDataRequest } from "gen/model/investmentEntityCreateEditAPIDataRequest";
import { InvestmentEntityReadData } from "gen/model/investmentEntityReadData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { investmentUpdateBody } from "gen/schemas/investment/investment";
import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

type PortfolioProfileSection = "basic" | "additional";

export const PortfolioTabProfile: React.FC = () => {
  const { t } = useTranslation(["investment", "common", "entity", "enums"]);
  const queryClient = useQueryClient();

  const { fundFamilyId, portfolioId } = useOutletContext<PortfolioTabProps>();

  const { data: investmentData, refetch } = useInvestmentRetrieveSuspense(
    fundFamilyId,
    portfolioId,
  );

  const [editingSection, setEditingSection] =
    useState<PortfolioProfileSection>();

  const onCloseComplete = useCallback(async () => {
    setEditingSection(undefined);
    await refetch();
    await queryClient.invalidateQueries({
      queryKey: getInvestmentTableDataRetrieveQueryKey(fundFamilyId),
    });
  }, [fundFamilyId, queryClient, refetch]);

  return (
    <PageTabBody data-private={true}>
      <ContentBlockGroup defaultIndex={[0, 1]}>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockActions>
              <Restricted permission={PermissionTypeEnum.config}>
                <ContentBlockMenu>
                  <MenuItem onClick={() => setEditingSection("basic")}>
                    {t("common:edit")}
                  </MenuItem>
                </ContentBlockMenu>
              </Restricted>
            </ContentBlockActions>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("investment:portfolioName.short")}>
                {investmentData.short_name}
              </Descriptions.Item>
              <Descriptions.Item label={t("entity:externalId")}>
                {investmentData.external_id}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:portfolioDescription")}>
                {investmentData.metadata.description}
              </Descriptions.Item>
              <Descriptions.Item label={<AllocationMethodologyTitle />}>
                {investmentData.metadata.is_allocable
                  ? t("investment:portfolio")
                  : t("investment:investment")}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("common:additionalInformation")}
            </ContentBlockTitle>
            <ContentBlockActions>
              <Restricted permission={PermissionTypeEnum.config}>
                <ContentBlockMenu>
                  <MenuItem
                    onClick={() => {
                      setEditingSection("additional");
                    }}
                  >
                    {t("common:edit")}
                  </MenuItem>
                </ContentBlockMenu>
              </Restricted>
            </ContentBlockActions>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("investment:acquisitionDate")}>
                {formatIsoDateToDisplay(
                  investmentData.metadata.acquisition_date,
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:dispositionDate")}>
                {formatIsoDateToDisplay(
                  investmentData.metadata.disposition_date,
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:investmentLocation")}>
                {formatInvestmentLocationCountry(
                  t,
                  investmentData.metadata.investment_location?.country,
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("investment:targetAmount")}>
                {formatMoney(investmentData.metadata.target_amount)}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("investment:realEstateInvestmentType")}
              >
                {investmentData.metadata.real_estate_investment_type
                  ? t(
                      `enums:RealEstateInvestmentTypeEnum.${investmentData.metadata.real_estate_investment_type}`,
                    )
                  : null}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      {editingSection && (
        <PortfolioEditDrawer
          fundFamilyId={fundFamilyId}
          portfolioId={portfolioId}
          onCloseComplete={onCloseComplete}
          section={editingSection}
          investmentData={investmentData}
        />
      )}
    </PageTabBody>
  );
};

interface PortfolioEditDrawerProps {
  fundFamilyId: string;
  portfolioId: string;
  onCloseComplete: () => void;
  section: PortfolioProfileSection;
  investmentData: InvestmentEntityReadData;
}

const PortfolioEditDrawer: React.FC<PortfolioEditDrawerProps> = ({
  fundFamilyId,
  portfolioId,
  onCloseComplete,
  section,
  investmentData,
}) => {
  const { t } = useTranslation(["common", "investment"]);

  const { mutateAsync: updateInvestment } = useInvestmentUpdate();

  const form = useForm<InvestmentEntityCreateEditAPIDataRequest>({
    resolver: zodResolver(investmentUpdateBody),
    defaultValues: investmentData,
  });

  const onSubmit = async (
    data: InvestmentEntityCreateEditAPIDataRequest,
  ): Promise<InvestmentEntityCreateEditAPIDataRequest> =>
    await updateInvestment({
      fundFamilyId,
      id: portfolioId,
      data,
    });

  return (
    <FormDrawer
      title={t("investment:editPortfolio.title")}
      formProps={{
        form,
        onSubmit,
        successMessage: t("investment:editPortfolio.success"),
      }}
      bodyProps={{
        "data-private": true,
      }}
      onCloseComplete={onCloseComplete}
    >
      {section === "basic" && <PortfolioFormRequiredFields />}
      {section === "additional" && <PortfolioFormAdditionalFields />}
    </FormDrawer>
  );
};
